import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast, Toaster } from "sonner";
import { Trash2, ShieldAlert } from "lucide-react";

export default function AccountDelete() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showFinal, setShowFinal] = useState(false);
    const [busy, setBusy] = useState(false);

    const canSubmit = password && confirm === "DELETE";

    const onDelete = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/me/delete", { password, confirm: "DELETE" });
            toast.success("Your account has been deleted.");
            // Clean up local session and bounce to home
            await logout();
            // Show a small summary then redirect after 4s
            setTimeout(() => nav("/", { replace: true }), 1500);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete account. Try again.");
        } finally {
            setBusy(false);
            setShowFinal(false);
        }
    };

    if (!user) {
        return (
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="account-delete-public">
                <div className="label-eyebrow text-sky-700">Account &amp; data</div>
                <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-2 mb-6">Delete your account</h1>
                <div className="bg-white border border-stone-200 p-8 space-y-4 text-stone-700 text-sm leading-relaxed">
                    <p>
                        To delete your Hahnemann account and the personal data we hold for you, please
                        <strong> sign in first</strong>. We require your password as a final security check before
                        permanently deleting your account.
                    </p>
                    <p>
                        If you've lost access to your account, email
                        <a href="mailto:help@hplmedicines.com" className="text-sky-700 underline ml-1">help@hplmedicines.com</a>
                        from the address on the account and our team will assist you within 7 business days
                        (in accordance with India's DPDP Act 2023).
                    </p>
                    <Button onClick={() => nav("/login?next=/account/delete")} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="acct-delete-signin">
                        Sign in to continue
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="account-delete-page">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-red-700">Permanent action</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-2 mb-6">Delete your account</h1>

            <div className="bg-amber-50 border border-amber-200 p-4 mb-6 flex gap-3 items-start">
                <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900">
                    <p className="font-semibold mb-1">This cannot be undone.</p>
                    <p>Once you delete your account, your password is invalidated immediately and you will lose access. Re-creating an account with the same email creates a fresh, unrelated profile.</p>
                </div>
            </div>

            <div className="bg-white border border-stone-200 p-6 space-y-5">
                <section>
                    <h2 className="font-serif text-xl text-stone-900 mb-2">What gets deleted</h2>
                    <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1">
                        <li>Your user account (<code>{user.email}</code>) and saved profile</li>
                        <li>Your shopping cart and saved addresses</li>
                        <li>Your AI Doctor &amp; support chatbot conversations</li>
                        <li>If you're a doctor — your doctor profile and earnings stay anonymised</li>
                        <li>If you're a distributor — your business profile</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-serif text-xl text-stone-900 mb-2">What we must keep</h2>
                    <p className="text-sm text-stone-700 mb-1">
                        For accounting, GST and audit compliance, the following records are retained — but your personal
                        identifiers (name, phone) are <strong>redacted in place</strong> so the records show as "Deleted user":
                    </p>
                    <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1">
                        <li>Past orders &amp; invoices</li>
                        <li>Doctor consultations you booked or conducted</li>
                        <li>Payments &amp; doctor payout records (if applicable)</li>
                    </ul>
                </section>

                <section className="pt-2 border-t border-stone-200">
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Your password</Label>
                    <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Required — for security"
                        className="rounded-none border-stone-300 mt-1"
                        data-testid="acct-delete-password"
                    />

                    <Label className="text-xs uppercase tracking-widest text-stone-500 mt-4 block">Type DELETE to confirm</Label>
                    <Input
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="DELETE"
                        className="rounded-none border-stone-300 mt-1 font-mono"
                        data-testid="acct-delete-confirm"
                    />
                </section>

                <Button
                    onClick={() => setShowFinal(true)}
                    disabled={!canSubmit || busy}
                    className="rounded-none w-full bg-red-700 hover:bg-red-800 text-stone-50"
                    data-testid="acct-delete-submit"
                >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete my account permanently
                </Button>
            </div>

            <AlertDialog open={showFinal} onOpenChange={setShowFinal}>
                <AlertDialogContent className="rounded-none" data-testid="acct-delete-final-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl text-red-700">Last chance</AlertDialogTitle>
                        <AlertDialogDescription>
                            Confirm: <strong>{user.email}</strong> will be permanently deleted. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="acct-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            disabled={busy}
                            className="rounded-none bg-red-700 hover:bg-red-800 text-stone-50"
                            data-testid="acct-delete-final-confirm"
                        >
                            {busy ? "Deleting…" : "Yes, delete forever"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
